<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <b-icon-cash-stack class="mr-1"></b-icon-cash-stack>
      {{ $t('page.title.withdrawals') }}
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('title.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="phrase"
                  filter-operator="like"
                  :placeholder="$t('filters.placeholder.manual_payment_phrase')"
                  :label="$t('filters.label.manual_payment_phrase')"
              >
              </filter-input>
            </div>
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="referenceId"
                  filter-operator="like"
                  :label="$t('filters.label.transaction_id')"
              >
              </filter-input>
            </div>
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="id"
                  filter-operator="like"
                  :label="$t('filters.label.payment_id')"
              >
              </filter-input>
            </div>
          </div> <!-- #row-->

          <div class="row">
            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="filterOptions.comparisonOperator[0]"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="amount"
                  :label="$t('filters.label.amount')"
              >
              </filter-input-with-operator>
            </div>
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.status"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="status"
                  :label="$t('filters.label.manual_status')"
                  :placeholder="$t('filters.placeholder.manual_status')">
              </filter-select>
            </div>
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="currency"
                  filter-operator="like"
                  :label="$t('filters.label.currency')"
              >
              </filter-input>
            </div>
          </div> <!-- #row-->
          <div class="row">
            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="createdAt"
                  open-direction="left"
                  :label="$t('filters.label.created_at')">
              </filter-date-range-picker>
            </div>
          </div> <!-- #row-->
        </div> <!-- #filters-->
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('title.list') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

          <div class="table-responsive">
            <b-table striped show-empty
                     :empty-text="$t('table.no_results')"
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template v-for='idSlot in ["id", "userId"]'
                        v-slot:[`cell(${idSlot})`]="data">
                {{ data.value | shortId }}
              </template>

              <template #cell(referenceId)="data">
                <router-link
                    :to="{ name: 'transactions-history', query: { transactionId: data.item.referenceId }}">
                  {{ data.value | shortId }}</router-link>
              </template>

              <template #cell(amount)="data">
                {{ data.value | numberFormat }}
              </template>

              <template #cell(userNickName)="data">
                <router-link
                    :to="{ name: 'player-details', params: { id: data.item.userId }}">
                {{ data.value  }}
                </router-link>
              </template>

              <template #cell(createdAt)="data">
                {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}
              </template>

              <template #cell(confirmRejectDate)="data">
                {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}
              </template>

              <template #cell(status)="data">
                <b-badge pill variant="warning" v-if="data.value === 'PENDING'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="danger" v-else-if="data.value === 'REJECTED'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="success" v-else>{{ $t(data.value) }}</b-badge>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <template v-if="data.item.status === 'PENDING'">
                    <b-button variant="success" size="sm"
                              @click="confirmTransaction(data.item)">
                      {{ $t('table.btn.confirm') }}
                    </b-button>
                    <b-button variant="danger" size="sm"
                              @click="cancelTransaction(data.item)">
                      {{ $t('table.btn.reject') }}
                    </b-button>
                    <b-button variant="primary" size="sm"
                              @click="openModal('show', data.item)">
                      {{ $t('table.btn.show') }}
                    </b-button>
                  </template>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                </div>
              </template>

            </b-table>
          </div> <!-- #table-responsive-->

          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

        </div>
      </template>
      <template v-slot:footer>

      </template>
    </page-card>


    <div class="card questions sygni-grid">
      <div class="card-header">
        <h3 class="card-title">
        </h3>
        <div class="d-flex card-header-pills float-right">
        </div>
      </div>
      <div class="card-body">
        <div class="row mt-2">
          <div class="col-12">
          </div>
        </div>
      </div> <!-- #card-body-->
    </div> <!-- #card questions sygni-grid-->
  </div>
</template>

<script>

import axios from "axios";
import FilterInputWithOperator from "../../../components/grid/filters/FilterInputWithOperator";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import GridMixin from "../../../components/grid/GridMixin";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import PageCard from "../../../components/page/PageCard";
import PageHeader from "../../../components/page/PageHeader";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import alertify from "alertifyjs";
import ShowWithdrawTransactionModal from "../components/ShowWithdrawTransactionModal";

export default {
  mixins: [GridMixin],
  components: {
    FilterSelect,
    FilterInput,
    FilterInputWithOperator,
    FilterDateRangePicker,
    GridPagination,
    PageCard,
    PageHeader
  },
  data() {
    return {
      gridGetUrl: '/api/resale/outgoing-manual-money-payment',
      deleteActionUrl: '/api/dictionary/',
      showDeleted: false,
      orderColumn: 'createdAt',
      orderDirection: false,
      fields: [
        {key: 'id', label: this.$t('table.header.payment_id')},
        {key: 'referenceId', label: this.$t('table.header.transaction_id')},
        {key: 'userNickName', label: this.$t('table.header.nick_name')},
        {key: 'userEmail', label: this.$t('table.header.email')},
        {key: 'amount', label: this.$t('table.header.amount'), sortable: true},
        {key: 'currency', label: this.$t('table.header.currency')},
        {key: 'createdAt', label: this.$t('table.header.resale_created_at'), sortable: true},
        {key: 'confirmRejectDate', label: this.$t('table.header.confirm_or_rejected_date')},
        {key: 'status', label: this.$t('table.header.manual_status'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions'),  sortable: false},
      ],
      selectOptions: {
        bulkActions: [
          {value: null, text: this.$t('table.bulk.actions')},
          {value: 'delete', text: this.$t('table.bulk.actions.delete')},
        ],
      },
      filterOptions: {
        comparisonOperator: [
          {value: 'eq', text: this.$t('EQUALS')},
          {value: 'gte', text: this.$t('GREATER_THAN')},
          {value: 'lte', text: this.$t('LOWER_THAN')},
          {value: 'btw', text: this.$t('BETWEEN')},
        ],
        currency: [
          {value: 'FUN_COINS', text: this.$t('FUN_COINS')},
          {value: 'QUIZ_COINS', text: this.$t('QUIZ_COINS')},
        ],
        status: [
          {value: 'CONFIRMED', text: this.$t('CONFIRMED')},
          {value: 'REJECTED', text: this.$t('REJECTED')},
          {value: 'PENDING', text: this.$t('PENDING')},
        ],
      },
      items: [],
    };
  },
  mounted() {
    this.$on('refresh-dict', () => {
      this.loadItems();
    })
  },
  methods: {
    createFilterQuery() {
      let filter = '';
      Object.keys(this.filterQuery).forEach(key => {
        filter += this.filterQuery[key]
          if(key==='status'){

              filter += '&filters[status][0][operator]=eq'
          }
      })
      return filter;
    },
    confirmTransaction(item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm(this.$t('modal.title.resale_id')+ this.$options.filters.shortId(item.id), {
        title: this.$t('modal.title.confirm_resale'),
        okTitle: this.$t('btn.confirm'),
        cancelTitle: this.$t('btn.cancel'),
        okVariant: 'success',
      })
          .then(value => {
            if (value) {
              axios.put(`/api/resale/outgoing-manual-money-payment/${item.id}/confirmation`)
                  .then(function () {
                    _that.loadItems();
                    alertify.success(_that.$t('notify.resale.confirmation.success'));
                  })
                  .catch(error => {
                    console.log(error);
                    alertify.error(_that.$t('notify.resale.confirmation.error'));
                  });
            }
          });
    },
    cancelTransaction(item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm(this.$t('modal.title.resale_id')+ this.$options.filters.shortId(item.id), {
        title: this.$t('modal.title.cancel_resale'),
        okTitle: this.$t('btn.confirm'),
        cancelTitle: this.$t('btn.cancel'),
        okVariant: 'warning',
      })
          .then(value => {
            if (value) {
              axios.put(`/api/resale/outgoing-manual-money-payment/${item.id}/rejection`)
                  .then(function () {
                    _that.loadItems();
                    alertify.success(_that.$t('notify.resale.rejection.success'));
                  })
                  .catch(error => {
                    console.log(error);
                    alertify.error(_that.$t('notify.resale.rejection.error'));
                  });
            }
          });
    },
    loadItems() {
      this.isBusy = true;
      let _that = this;
      let offset = this.perPage * (this.currentPage - 1);
      let fullUrl = this.gridGetUrl + '?limit='
          + this.perPage
          + '&offset=' + offset
          + this.createSortQuery() + this.createFilterQuery();
      // +'&filters[paymentMethod][0][value]=MANUAL_PAYMENT_METHOD';
      axios.get(fullUrl)
          .then(function (response) {
            _that.items = response.data.items;
            _that.totalCount = response.data.totalCount;
            _that.isBusy = false;
          })
          .catch(error => {
            console.log(error);
          });
    },
    toggleAllCheckboxes: function (checked) {
      this.selectedRows = [];
      if (checked) {
        let _that = this;
        this.items.forEach((value) => {
          _that.selectedRows.push(value.id);
        })
      }
    },
    goTo(item) {
      this.$router.push({name: 'dictionary-entry', params: {entry: item.alias}});
    },
    edit(item) {
      this.$router.push({name: 'dictionary-entry', params: {entry: item.alias}});
    },
    deleteItem: function (item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm(this.$t('modal.title.transaction_id')+ item.alias, {
        title: this.$t('modal.title.delete_transaction'),
        okTitle: this.$t('btn.delete'),
        cancelTitle: this.$t('btn.cancel'),
        okVariant: 'danger',
      })
          .then(value => {
            if (value) {
              axios.delete(`/api/dictionary/${item.alias}`)
                  .then(function () {
                    _that.loadItems();
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    },
    openModal(type, data) {
      switch (type) {
        case('show'): {
          this.$store.commit('modal/openModal', {
            parent: this,
            modal: ShowWithdrawTransactionModal,
            modalName: 'showWithdrawTransactionModal',
            props: {transactionDetails: data}
          });
          break;
        }
      }
    }
  },
  watch: {
  }
}
</script>
